"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const ProjectTypeContext = createContext();
export const useProjectType = () => useContext(ProjectTypeContext);

export const ProjectTypeProvider = ({ children }) => {
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Project Types
  const fetchProjectTypes = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${BASE_URL}/project-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/";
        return;
      }

      if (!res.ok) {
        setError("Failed to fetch project types");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setProjectTypes(data.data || []);
      } else {
        setError(data.message || "Failed to fetch project types");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Error fetching project types");
    } finally {
      setLoading(false);
    }
  };

  // Create Project Type
  const createProjectType = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/project-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to create project type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchProjectTypes();

        toast.success(data.message || "Project type created successfully");
      }

      return data;
    } catch (err) {
      console.error("Create error:", err);

      return {
        success: false,
        message: err.message || "Error creating project type",
      };
    }
  };

  // Update Project Type
  const updateProjectType = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/project-types/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update project type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchProjectTypes();

        toast.success(data.message || "Project type updated successfully");
      }

      return data;
    } catch (err) {
      console.error("Update error:", err);

      return {
        success: false,
        message: err.message || "Error updating project type",
      };
    }
  };

  // Delete Project Type
  const deleteProjectType = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/project-types/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete project type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchProjectTypes();

        toast.success(data.message || "Project type deleted successfully");
      }

      return data;
    } catch (err) {
      console.error("Delete error:", err);

      return {
        success: false,
        message: err.message || "Error deleting project type",
      };
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchProjectTypes();
    }
  }, []);

  return (
    <ProjectTypeContext.Provider
      value={{
        projectTypes,
        loading,
        error,
        fetchProjectTypes,
        createProjectType,
        updateProjectType,
        deleteProjectType,
      }}
    >
      {children}
    </ProjectTypeContext.Provider>
  );
};